import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import type {
  InstrumentDto,
  InstrumentSearchKind,
  QuoteDto,
} from "../shared/contracts.ts";
import { changeClass, formatMoney, formatPercent, numberValue } from "./format.ts";
import {
  compareTableValues,
  SortableHeader,
  toggleSort,
  useResizableColumns,
  type SortState,
  type TableColumn,
} from "./table.tsx";
import { INSTRUMENT_GROUPS, instrumentGroupKey } from "./watchlist.ts";

type InstrumentSortKey = "instrument" | "price" | "changePercent";

const INSTRUMENT_COLUMNS = [
  { defaultDirection: "asc", key: "instrument", label: "Инструмент", minWidth: 105, width: 132 },
  { defaultDirection: "desc", key: "price", label: "Цена", minWidth: 72, width: 82 },
  { defaultDirection: "desc", key: "changePercent", label: "За день, %", minWidth: 78, width: 86 },
] as const satisfies readonly TableColumn<InstrumentSortKey>[];

const SEARCH_KINDS: readonly { kind?: InstrumentSearchKind; label: string }[] = [
  { label: "Все" },
  { kind: "INSTRUMENT_TYPE_SHARE", label: "Акции" },
  { kind: "INSTRUMENT_TYPE_FUTURES", label: "Фьючерсы" },
  { kind: "INSTRUMENT_TYPE_BOND", label: "Облигации" },
  { kind: "INSTRUMENT_TYPE_ETF", label: "Фонды" },
  { kind: "INSTRUMENT_TYPE_CURRENCY", label: "Валюта и металлы" },
];

function logoUrl(instrument: InstrumentDto): string | undefined {
  if (!instrument.brandLogoName) return undefined;
  const logoName = instrument.brandLogoName.replace(/\.(?:png|svg)$/i, "");
  return `https://invest-brands.cdn-tinkoff.ru/${encodeURIComponent(logoName)}x160.png`;
}

function InstrumentIcon({ instrument }: { instrument: InstrumentDto }) {
  const url = logoUrl(instrument);
  const brandColor = /^#[0-9a-f]{6}$/i.test(instrument.brandColor ?? "")
    ? instrument.brandColor
    : undefined;
  const textColor = /^#[0-9a-f]{6}$/i.test(instrument.brandTextColor ?? "")
    ? instrument.brandTextColor
    : undefined;
  return (
    <span
      className="watchlist-instrument-icon"
      style={{ backgroundColor: brandColor, color: textColor }}
    >
      <span>{instrument.ticker.slice(0, 2)}</span>
      {url && <img alt="" onError={(event) => { event.currentTarget.hidden = true; }} src={url} />}
    </span>
  );
}

function sortValue(
  instrument: InstrumentDto,
  quote: QuoteDto | undefined,
  key: InstrumentSortKey,
): number | string | undefined {
  if (key === "instrument") return `${instrument.ticker} ${instrument.name}`;
  if (key === "price") return numberValue(quote?.price);
  return quote?.changePercent;
}

function readCollapsed(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem("terminal.watchlist.collapsed") ?? "[]") as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

function selectOnKeyboard(event: ReactKeyboardEvent, select: () => void): void {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  select();
}

interface InstrumentsProps {
  instruments: InstrumentDto[];
  onAdd: (instrument: InstrumentDto) => void;
  onRemove: (uid: string) => void;
  onSelect: (uid: string) => void;
  quotes: Record<string, QuoteDto>;
  selectedUid?: string;
}

export function InstrumentsWidget({
  instruments,
  onAdd,
  onRemove,
  onSelect,
  quotes,
  selectedUid,
}: InstrumentsProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [context, setContext] = useState<{ uid: string; x: number; y: number }>();
  const [sort, setSort] = useState<SortState<InstrumentSortKey>>();
  const columns = useResizableColumns(INSTRUMENT_COLUMNS, "terminal.table.instruments.columns");
  const groups = useMemo(() => INSTRUMENT_GROUPS.map((group) => ({
    ...group,
    instruments: instruments
      .filter((instrument) => instrumentGroupKey(instrument.instrumentType) === group.key)
      .toSorted((left, right) => sort
        ? compareTableValues(
            sortValue(left, quotes[left.uid], sort.key),
            sortValue(right, quotes[right.uid], sort.key),
            sort.direction,
          )
        : 0),
  })).filter((group) => group.instruments.length > 0), [instruments, quotes, sort]);

  const toggleGroup = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem("terminal.watchlist.collapsed", JSON.stringify([...next]));
      return next;
    });
  };

  return (
    <section className="widget instruments-widget" data-widget="instruments">
      <header className="widget-header instruments-widget-header">
        <span className="widget-title">Инструменты</span>
        <button aria-label="Добавить инструмент" className="instrument-add-button" onClick={() => setAddOpen(true)} type="button">+</button>
      </header>
      <div className="table-scroll instruments-table-scroll">
        <table className="data-table instruments-table" style={{ minWidth: columns.totalWidth, width: columns.totalWidth }}>
          <colgroup>{INSTRUMENT_COLUMNS.map((column) => <col key={column.key} style={{ width: columns.widths[column.key] }} />)}</colgroup>
          <thead><tr>{INSTRUMENT_COLUMNS.map((column) => (
            <SortableHeader
              column={column}
              currentWidth={columns.widths[column.key]}
              key={column.key}
              onResizeBy={columns.resizeBy}
              onResizeStart={columns.startResize}
              onSort={(key, direction) => setSort((current) => toggleSort(current, key, direction))}
              sort={sort}
            />
          ))}</tr></thead>
          <tbody>
            {groups.map((group) => [
              <tr className="group-row instruments-group-row" key={`group-${group.key}`}>
                <td colSpan={3}>
                  <button
                    aria-expanded={!collapsed.has(group.key)}
                    onClick={() => toggleGroup(group.key)}
                    type="button"
                  >
                    <span>{group.label}</span><i aria-hidden="true">{collapsed.has(group.key) ? "⌄" : "⌃"}</i>
                  </button>
                </td>
              </tr>,
              ...(collapsed.has(group.key) ? [] : group.instruments.map((instrument) => {
                const quote = quotes[instrument.uid];
                return (
                  <tr
                    aria-label={`Выбрать ${instrument.ticker}`}
                    className={`selectable-instrument-row watchlist-row ${selectedUid === instrument.uid ? "selected" : ""}`}
                    data-instrument-uid={instrument.uid}
                    key={instrument.uid}
                    onClick={() => onSelect(instrument.uid)}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      setContext({
                        uid: instrument.uid,
                        x: Math.min(event.clientX, window.innerWidth - 188),
                        y: Math.min(event.clientY, window.innerHeight - 54),
                      });
                    }}
                    onKeyDown={(event) => selectOnKeyboard(event, () => onSelect(instrument.uid))}
                    tabIndex={0}
                  >
                    <td><span className="watchlist-instrument-cell"><InstrumentIcon instrument={instrument} /><strong title={instrument.name}>{instrument.ticker}</strong></span></td>
                    <td>{formatMoney(quote?.price, instrument.currency)}</td>
                    <td className={changeClass(quote?.changePercent)}>{formatPercent(quote?.changePercent).replace(/^\+/, "")}</td>
                  </tr>
                );
              })),
            ])}
          </tbody>
        </table>
        {instruments.length === 0 && <div className="empty-state">Список пуст. Нажмите +, чтобы добавить инструмент.</div>}
      </div>
      {context && <InstrumentContextMenu
        onClose={() => setContext(undefined)}
        onRemove={() => {
          onRemove(context.uid);
          setContext(undefined);
        }}
        x={context.x}
        y={context.y}
      />}
      {addOpen && <InstrumentSearchDialog
        instruments={instruments}
        onAdd={onAdd}
        onClose={() => setAddOpen(false)}
      />}
    </section>
  );
}

function InstrumentContextMenu({
  onClose,
  onRemove,
  x,
  y,
}: {
  onClose: () => void;
  onRemove: () => void;
  x: number;
  y: number;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", onClose, true);
    return () => {
      window.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  return createPortal(
    <div className="watchlist-context-menu" ref={menuRef} role="menu" style={{ left: x, top: y }}>
      <button onClick={onRemove} role="menuitem" type="button"><span aria-hidden="true">×</span>Удалить из списка</button>
    </div>,
    document.body,
  );
}

function InstrumentSearchDialog({
  instruments,
  onAdd,
  onClose,
}: {
  instruments: InstrumentDto[];
  onAdd: (instrument: InstrumentDto) => void;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<InstrumentSearchKind>();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<InstrumentDto[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = new Set(instruments.map((instrument) => instrument.uid));

  useEffect(() => {
    inputRef.current?.focus();
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < 2) {
      setResults([]);
      setState("idle");
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setState("loading");
      const params = new URLSearchParams({ query: normalized });
      if (kind) params.set("kind", kind);
      try {
        const response = await fetch(`/api/instruments/search?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error("search_failed");
        const payload = await response.json() as { instruments: InstrumentDto[] };
        setResults(payload.instruments);
        setState("ready");
      } catch (error) {
        if ((error as Error).name !== "AbortError") setState("error");
      }
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [kind, query]);

  return createPortal(
    <div className="instrument-search-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section aria-label="Добавление инструмента" aria-modal="true" className="instrument-search-dialog" role="dialog">
        <header>
          <div className="instrument-search-field">
            <span aria-hidden="true" className="dialog-search-icon" />
            <input
              aria-label="Поиск по тикеру или названию инструмента"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Поиск по тикеру или названию инструмента"
              ref={inputRef}
              value={query}
            />
          </div>
          <button aria-label="Закрыть" className="instrument-dialog-close" onClick={onClose} type="button">×</button>
        </header>
        <div className="instrument-kind-filters">
          {SEARCH_KINDS.map((option) => (
            <button
              className={kind === option.kind ? "active" : ""}
              key={option.label}
              onClick={() => setKind(option.kind)}
              type="button"
            >{option.label}</button>
          ))}
        </div>
        <div className="instrument-search-results table-scroll">
          {state === "idle" && <div className="instrument-search-message">Введите минимум два символа</div>}
          {state === "loading" && <div className="instrument-search-message">Поиск…</div>}
          {state === "error" && <div className="instrument-search-message negative">Не удалось выполнить поиск</div>}
          {state === "ready" && results.length === 0 && <div className="instrument-search-message">Ничего не найдено</div>}
          {results.map((instrument) => {
            const added = selected.has(instrument.uid);
            return (
              <button
                className="instrument-search-result"
                disabled={added}
                key={instrument.uid}
                onClick={() => onAdd(instrument)}
                type="button"
              >
                <InstrumentIcon instrument={instrument} />
                <span><strong>{instrument.name}</strong><small>{instrument.ticker}</small></span>
                <i>{added ? "Добавлено" : instrument.ticker}</i>
              </button>
            );
          })}
        </div>
      </section>
    </div>,
    document.body,
  );
}
